import React, { InputHTMLAttributes, useRef } from 'react'
import classNames from 'classnames'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { IconColor } from '@/atoms/utils'
import { ReactFCC } from '@/types/react'

interface CheckBoxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  checkedClassName?: string
  uncheckedClassName?: string
  labelClassName?: string
  wrapperClassName?: string
  checkboxSize?: number
  color?: IconColor
  size?: 'sm' | 'md'
}

export const CheckBox: ReactFCC<CheckBoxProps> = ({
  checked,
  children,
  color,
  onChange,
  onBlur,
  className,
  checkedClassName,
  uncheckedClassName,
  labelClassName,
  checkboxSize,
  wrapperClassName,
  id = 'check-box',
  size = 'md',
  ...props
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null)

  const commonClasses = classNames(
    'flex items-center justify-center',
    'shrink-0',
    'focus-visible:ring-transparent',
    'focus-visible:ring-0',
    'focus-visible:ring-offset-0',
    'focus-visible:ring-offset-transparent',
    {
      'w-5 h-5 min-w-5 min-h-5 rounded': size === 'sm',
      'w-6 h-6 min-w-6 min-h-6 rounded-lg md:w-8 md:h-8': size === 'md',
    },
  )
  const uncheckedClasses = classNames(commonClasses, 'border-2', 'border-core-gray-400', uncheckedClassName)
  const checkedClasses = classNames(commonClasses, 'bg-oxide', checkedClassName)
  const iconSizeMobile = checkboxSize ?? (size === 'sm' ? 16 : 24)
  const iconSizeDesktop = checkboxSize ?? (size === 'sm' ? 16 : 32)

  const handleCheckBoxClick = () => {
    checkboxRef.current?.click()
  }

  return (
    <div className={classNames('flex items-center justify-items-center', wrapperClassName)}>
      {checked ? (
        <div onClick={handleCheckBoxClick} className={classNames(checkedClasses)}>
          <CheckIcon color={color} className="block md:hidden" size={iconSizeMobile} />
          <CheckIcon color={color} className="hidden md:block" size={iconSizeDesktop} />
        </div>
      ) : (
        <div onClick={handleCheckBoxClick} className={classNames(uncheckedClasses)} />
      )}
      <label
        htmlFor={id}
        className={labelClassName ? labelClassName : 'ml-2 flex cursor-pointer items-center py-4 text-xs'}
      >
        {children}
      </label>
      <input
        id={id}
        ref={checkboxRef}
        checked={checked}
        data-testid={children}
        type="checkbox"
        className={classNames('hidden', className)}
        onChange={onChange}
        onBlur={onBlur}
        {...props}
      />
    </div>
  )
}
